/**
 * MentionAutocomplete Component
 *
 * Dropdown autocomplete for @mentions in chat input.
 * Shows matching identities with their service availability.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { Avatar, Spinner, Link } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { useMentions, useAddRecentIdentity } from "@/hooks/useMentions";
import { IdentitySearchResult } from "@/stores/MentionStore";

import { ServiceBadge, SUPPORTED_SERVICES } from "./ServiceBadge";

interface MentionAutocompleteProps {
  /** Search query (text after @) */
  query: string;
  /** Called when user selects an identity */
  onSelect: (identity: IdentitySearchResult) => void;
  /** Called when autocomplete should close */
  onClose: () => void;
  /** Position of the dropdown */
  position: { top: number; left: number };
  /** Whether the dropdown is visible */
  visible: boolean;
}

/**
 * MentionAutocomplete displays a dropdown of matching identities
 * when the user types @ in the chat input.
 *
 * Features:
 * - Keyboard navigation (up/down arrows, enter to select, escape to close)
 * - Shows service availability badges (Slack, Email, Teams, Google)
 * - Displays recent identities when query is empty
 * - Caches results for performance
 *
 * @example
 * <MentionAutocomplete
 *   query="joh"
 *   onSelect={handleSelect}
 *   onClose={handleClose}
 *   position={{ top: 100, left: 50 }}
 *   visible={showAutocomplete}
 * />
 */
export const MentionAutocomplete: React.FC<MentionAutocompleteProps> = ({
  query,
  onSelect,
  onClose,
  position,
  visible,
}) => {
  const { t } = useTranslation();
  const { results, loading, error, setQuery } = useMentions();
  const addRecentIdentity = useAddRecentIdentity();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Update search query when prop changes
  useEffect(() => {
    setQuery(query);
    setSelectedIndex(0);
  }, [query, setQuery]);

  // Handle identity selection
  const handleSelect = useCallback(
    (identity: IdentitySearchResult) => {
      addRecentIdentity(identity);
      onSelect(identity);
    },
    [onSelect, addRecentIdentity],
  );

  // Keyboard navigation
  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
          break;
        case "Enter":
          e.preventDefault();
          if (results[selectedIndex]) {
            handleSelect(results[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
        case "Tab":
          // Close on tab
          onClose();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visible, results, selectedIndex, handleSelect, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (!containerRef.current) return;
    const selectedEl = containerRef.current.querySelector(`[data-index="${selectedIndex}"]`);

    if (selectedEl) {
      selectedEl.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  if (!visible) return null;

  return (
    <div
      ref={containerRef}
      aria-label={t("Mention suggestions")}
      className="fixed z-50 bg-content1 border border-divider rounded-lg shadow-lg max-h-64 overflow-y-auto min-w-[280px]"
      role="listbox"
      style={{
        top: position.top,
        left: position.left,
        transform: "translateY(-100%)",
      }}
      tabIndex={-1}
      onMouseDown={(e) => e.preventDefault()}
    >
      {/* Header */}
      <div className="px-3 py-2 border-b border-divider">
        <span className="text-xs text-default-500">
          {query.length === 0 ? t("Recent mentions") : t("Search results")}
        </span>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-4">
          <Spinner size="sm" />
        </div>
      )}

      {/* Error state */}
      {error && <div className="px-3 py-4 text-sm text-danger">{error}</div>}

      {/* Empty state */}
      {!loading && !error && results.length === 0 && (
        <div className="px-3 py-4 text-sm text-default-500 text-center">
          {query.length > 0 ? t("No matches found") : t("No recent mentions")}
        </div>
      )}

      {/* Results list */}
      {!loading &&
        !error &&
        results.map((identity, index) => (
          <div
            key={identity.id}
            aria-selected={index === selectedIndex}
            className={`
              px-3 py-2 cursor-pointer flex items-center gap-3
              hover:bg-default-100 transition-colors
              ${index === selectedIndex ? "bg-default-100" : ""}
            `}
            data-index={index}
            role="option"
            tabIndex={0}
            onClick={() => handleSelect(identity)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleSelect(identity);
              }
            }}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            {/* Avatar */}
            <Avatar
              className="flex-shrink-0"
              name={identity.displayName}
              size="sm"
              src={identity.avatarUrl}
            />

            {/* Name and email */}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{identity.displayName}</div>
              <div className="text-xs text-default-500 truncate">{identity.canonicalEmail}</div>
            </div>

            {/* Service badges */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {SUPPORTED_SERVICES.map((service) => (
                <ServiceBadge
                  key={service}
                  available={identity.availableServices.includes(service)}
                  service={service}
                />
              ))}
            </div>
          </div>
        ))}

      {/* Footer hint */}
      <div className="px-3 py-2 border-t border-divider text-xs text-default-400 flex items-center justify-between">
        <div>
          <span className="mr-4">
            <kbd className="px-1 py-0.5 bg-default-100 rounded text-[10px]">↑↓</kbd> {t("navigate")}
          </span>
          <span className="mr-4">
            <kbd className="px-1 py-0.5 bg-default-100 rounded text-[10px]">Enter</kbd>{" "}
            {t("select")}
          </span>
          <span>
            <kbd className="px-1 py-0.5 bg-default-100 rounded text-[10px]">Esc</kbd> {t("close")}
          </span>
        </div>
        <Link
          className="text-xs text-primary hover:underline flex items-center gap-1"
          href="/settings#mentions"
          rel="noopener noreferrer"
          size="sm"
          target="_blank"
        >
          <Icon className="w-3 h-3" icon="solar:settings-linear" />
          {t("Add services")}
        </Link>
      </div>
    </div>
  );
};

export default MentionAutocomplete;
